/**
 * Zentrales Glossar. Jeder Fachbegriff steht GENAU EINMAL hier und wird per
 * <Hint term="..." /> ueberall in der UI erklärt. Deutsch, C-Level-tauglich.
 */
export type GlossarEintrag = {
  titel: string;
  kurz: string;
  lang?: string;
};

export const GLOSSAR: Record<string, GlossarEintrag> = {
  prozess_run: {
    titel: "Vorgang (Run)",
    kurz: "Ein protokollierter Durchlauf eines Prozessschritts: wer hat wann was angestossen. Jeder Vorgang erscheint sofort unter 'Agent Runs & Evidence'.",
    lang: "Technisch legt das System einen Agent-Run mit Zeitstempel, zustaendigem KI-Agenten und Trace-Referenz an - mandantenbezogen und dauerhaft gespeichert. So bleibt jede Aktivitaet im Programm nachvollziehbar.",
  },
  prozess_evidence: {
    titel: "Nachweis (Evidence)",
    kurz: "Der pruefbare Beleg, dass ein Schritt mit Ergebnis abgeschlossen wurde - eine kurze Notiz, was erledigt ist. Erst mit Nachweis zaehlt der Schritt als erledigt.",
    lang: "Die Notiz wird dauerhaft mit dem Vorgang gespeichert und mit einer Beleg-Adresse (Evidence-Bundle) verknuepft. Für Audit und Compliance laesst sich so jederzeit zeigen, wer wann was abgeschlossen hat.",
  },
  prozess_schritt: {
    titel: "Prozessschritt (Teilprozess)",
    kurz: "Ein Arbeitsschritt der Beratungs-Methodik (z. B. '1.2.4 Vertragsgestaltung') mit definierten Eingaben, Ergebnissen und einem zustaendigen KI-Agenten.",
    lang: "Die Nummer liest sich Phase.Abschnitt.Schritt. Die 59 Schritte in 12 Hauptphasen sind der Standard-Fahrplan von der Analyse bis zum Betrieb. Der Projektplan des Kundenprogramms (OpenProject) bleibt davon getrennt.",
  },
  mandant: {
    titel: "Mandant (Tenant)",
    kurz: "Ihr abgeschotteter Datenraum. Alles - Kennzahlen, Quellen, Baeume, Briefings - gehört genau einem Mandanten.",
    lang: "Technisch traegt jede Zeile in der Datenbank eine tenant_id; die Datenbank erzwingt die Trennung zusätzlich per Row-Level-Security. Ein anderer Mandant kann Ihre Zahlen auch bei einem Programmfehler nicht sehen.",
  },
  kpi: {
    titel: "KPI / Kennzahl",
    kurz: "Eine gemessene Größe mit Einheit, Richtung und Zielwert - z. B. 'Loesungsquote Beschwerden, %, hoeher ist besser, Ziel 70'.",
    lang: "Die Definition (der 'Metric Contract') ist von den Messwerten getrennt. Erst wenn beides da ist, zeigt das Cockpit einen Ist-Wert und einen Zielerreichungsgrad.",
  },
  metric_contract: {
    titel: "Metric Contract",
    kurz: "Die verbindliche Definition einer Kennzahl: Name, Einheit, Richtung (hoeher/niedriger ist besser), Zielwert.",
    lang: "Ohne Contract gibt es keine Zielerreichung - ein Messwert allein sagt nicht, ob 60 % gut oder schlecht sind.",
  },
  ist_wert: {
    titel: "Ist-Wert",
    kurz: "Der zuletzt gemessene Wert dieser Kennzahl - er stammt immer aus einer angeschlossenen Quelle, nie aus einer Schaetzung.",
  },
  platzhalter: {
    titel: "Platzhalter",
    kurz: "Für diese Kennzahl liegt noch KEIN Messwert vor. Die Zahl auf der Karte ist ein Fuellwert und darf nicht interpretiert werden.",
    lang: "Wir zeigen sie trotzdem an, damit sichtbar bleibt, welche Kennzahl noch Daten braucht. Sobald Werte über die Landing-Zone eingelesen werden, wird die Karte automatisch 'gemessen'.",
  },
  zielerreichung: {
    titel: "Zielerreichungsgrad",
    kurz: "Wie weit der Ist-Wert vom Zielwert entfernt ist - richtungsbewusst gerechnet.",
    lang: "Bei 'hoeher ist besser': Ist / Ziel. Bei 'niedriger ist besser': Ziel / Ist. 100 % = Ziel erreicht. Beispiel: Loesungsquote Ist 60 %, Ziel 70 % -> 85,7 % Zielerreichung.",
  },
  quelle: {
    titel: "Datenquelle",
    kurz: "Ein angeschlossenes System, aus dem Werte oder Belege stammen (OpenProject, OpenSearch-Indizes, das Lakehouse-Gold).",
  },
  trust_gate: {
    titel: "Data-Trust-Gate",
    kurz: "Die Qualitätsschranke vor dem Cockpit: Erst wenn die Quellen erreichbar, aktuell und ausreichend gefuellt sind, gibt das System Kennzahlen frei.",
    lang: "Das Gate ist bewusst streng: Lieber ein leeres Cockpit mit Begründung als erfundene Zahlen. Jede Prüfung ist einzeln sichtbar - inklusive der Belege (Evidence), auf die sie sich stuetzt.",
  },
  quality_score: {
    titel: "Quality Score",
    kurz: "Punktwert je Quelle (0-100) aus echten Pruefungen: Ist die Quelle erreichbar? Wie alt ist der letzte Sync? Wie viele Datensaetze liegen vor?",
    lang: "Ab 75 Punkten gilt eine Quelle als vertrauenswürdig ('trusted'). Die Schwelle steht in der Server-Konfiguration (KPI_TRUST_THRESHOLD).",
  },
  driver_tree: {
    titel: "Driver-Tree (Werttreiberbaum)",
    kurz: "Die Ursache-Wirkungs-Landkarte: Welcher Hebel wirkt auf welche Kennzahl - und wie stark?",
    lang: "Oben steht der Wertbeitrag, darunter die Handlungsfelder, unten die messbaren Kennzahlen. Knoten mit hinterlegter Kennzahl zeigen Ist-Wert, Ziel und Zielerreichung; die Kanten tragen Gewicht und Konfidenz.",
  },
  briefing: {
    titel: "Briefing",
    kurz: "Ein Management-Bericht in Prosa: Was hat sich verändert, wo gibt es Verzug, welche Maßnahme wird empfohlen.",
    lang: "Wird aus den echten Daten des Mandanten erzeugt (Kennzahlen, Zielerreichung, Arbeitspaket-Status) - nicht aus allgemeinem Weltwissen.",
  },
  agent_run: {
    titel: "Agent-Run",
    kurz: "Ein protokollierter Lauf eines Agenten. Jeder Lauf hat Status, Zeitstempel, Belege und eine Hash-Kette - damit später nachvollziehbar ist, wer was womit erzeugt hat.",
  },
  evidence: {
    titel: "Evidence / Beleg",
    kurz: "Der Nachweis, worauf sich eine Aussage stuetzt - Quelle, Datensatz, Zeitpunkt, Hash.",
  },
  lakehouse: {
    titel: "Lakehouse (Bronze/Silver/Gold)",
    kurz: "Die Datenstrecke: Rohdaten (Bronze) werden geprüft und vereinheitlicht (Silver) und zu auswertbaren Sichten verdichtet (Gold). Das Cockpit liest aus Gold bzw. der Wertetabelle.",
  },
  steuerkreis: {
    titel: "Steuerkreis-Cockpit",
    kurz: "Die eine Seite für den Steuerkreis-Termin: Programmfortschritt, KPI-Ampeln, Gate-Matrix, letztes KI-Briefing und Datenlage auf einen Blick.",
  },
  gate_matrix: {
    titel: "Phasen-Gate-Matrix",
    kurz: "Ressort x Phase: für jedes Ressort und jede Projektphase eine Ampel - gruen (im Plan), gelb (nicht begonnen), rot (in Verzug).",
    lang: "Die Werte stammen aus dem OE-Board (Whiteboard-Stand), nicht aus einer Schaetzung. Fehlt ein Status, bleibt das Feld grau.",
  },
  teilprojekt: {
    titel: "Teilprojekt / Prozessschritt",
    kurz: "Ein abgegrenztes Arbeitspaket der Transformation. Der Fortschritt zaehlt erledigte Prozessschritte gegen die Gesamtzahl.",
  },
  frag_die_daten: {
    titel: "Frag die Daten",
    kurz: "Ein Chat-Feld, das eine Frage AUSSCHLIESSLICH aus den echten Programmdaten beantwortet - mit dem lokalen Modell, ohne Weltwissen.",
    lang: "Grundlage ist derselbe Faktenblock wie beim Briefing. Fehlt eine Information in den Daten, sagt die Antwort das ausdruecklich, statt zu raten.",
  },
  openproject: {
    titel: "OpenProject",
    kurz: "Das Projektsystem des 1&1-Programms. Arbeitspakete (WP) von dort hängen an den Knoten des Driver-Trees.",
  },
  raci: {
    titel: "RACI (Rollenmodell)",
    kurz: "Wer macht was: Verantwortlich (führt aus), Rechenschaft (steht dafür ein), Konsultiert (liefert zu/prüft), Informiert (wird auf dem Laufenden gehalten).",
    lang: "Im gefuehrten Prozessschritt heißt das konkret: Die Berater:in führt den Schritt durch und schließt ihn mit Nachweis ab; der Kunde liefert Eingaben (Notizen, Links, Unterlagen) und prüft Zwischenstaende; der Steuerkreis wird über Cockpit und OE-Board informiert.",
  },
  wissensbasis: {
    titel: "Wissensbasis (Hybrid-Suche)",
    kurz: "Alle ingestieren Plattform-Inhalte (Nextcloud-Dokumente, Protokolle, Ablagen) — semantisch durchsuchbar. Der Schritt zeigt automatisch passende Dokumente an.",
    lang: "Technisch: BM25-Volltextsuche plus Vektor-Suche (lokales bge-m3-Embedding), zusammengefuehrt per RRF. Neue Dokumente aus der Dropzone/Docling-Strecke erscheinen automatisch, sobald sie ingestiert sind. Es verlaesst kein Byte die Plattform.",
  },
  dry_run: {
    titel: "Dry-Run (Probelauf)",
    kurz: "Das System zeigt die geplante Aktion vollständig an (z. B. einen OpenProject-Kommentar), führt sie aber NICHT aus. Live-Schreiben erfordert eine ausdrueckliche Freigabe der Inhaberin.",
  },
  wp_zaehlung: {
    titel: "108 vs. 16 Arbeitspakete",
    kurz: "Kein Widerspruch: Das Cockpit zeigt alle 108 Arbeitspakete (volle Programmsicht); Chatbot/1und1os zeigen die 16 kundenfreigegebenen (kuratierter Aufnahmevertrag, 6 Top-Level-Projekte).",
    lang: "Zwei bewusste Lesepfade auf dieselbe OpenProject-Instanz. 108 = vollstaendige Steuerungs-/Reporting-Sicht über beide 1&1-Projektbaeume. 16 = eng gefasster, freigegebener Governance-Scope. Die 92 WPs in Unterprojekten (z. B. TP_302) liegen ausserhalb der Freigabe-Allowlist.",
  },
  quality_gate: {
    titel: "Qualitäts-Gate (PASS / REVIEW / BLOCK)",
    kurz: "Die Prüfschranke des Lakehouse: PASS = geprüft und veröffentlicht. REVIEW = wartet auf menschlichen Prüfentscheid (bei sensiblen Inhalten automatisch). BLOCK = erreicht keine Antworten.",
    lang: "So kommt nichts Ungeprüftes in Cockpits, Briefings oder Chat-Antworten. Schlägt eine automatische Prüfung (dbt-Test) fehl, bricht die Verarbeitungskette ab, statt fehlerhafte Daten zu veröffentlichen.",
  },
  gold: {
    titel: "Gold (geprüfte Wahrheit)",
    kurz: "Die oberste Stufe des Lakehouse: bereinigte, geprüfte, auswertbare Daten. Cockpits und Chatbots lesen ausschließlich hieraus.",
    lang: "Der Weg: Rohdaten (Bronze) -> bereinigt/dedupliziert (Silver) -> geprüfte Sichten (Gold). Korrekturen laufen immer über die Quelle durch diese Kette - nie direkt im Index.",
  },
  steuerungsfall: {
    titel: "Steuerungsfall",
    kurz: "Ein Vorgang im Steuerungssystem, der Auffälligkeit, Ursache, Entscheidung, Maßnahme und gemessene Wirkung zu einer nachvollziehbaren Kette verbindet.",
    lang: "Erst wenn nach der Umsetzung die Wirkung erfasst und der nächste Messzeitpunkt gesetzt ist, gilt der Regelkreis als geschlossen.",
  },
  fachpaket: {
    titel: "Fachpaket",
    kurz: "Ein wiederverwendbarer, freigegebener Wissensbaustein (Branche, Prozess, KPI oder Anwendungsfall), den die Beratung für den Kunden übernehmen kann.",
    lang: "Ursprung und kundenspezifische Ausprägung bleiben getrennt sichtbar - bewährtes Wissen wird genutzt, ohne die Herkunft zu verlieren.",
  },
  meetily: {
    titel: "Meetily",
    kurz: "Das Besprechungs-Werkzeug der Plattform. In die Wissensbasis gelangt nur die freigegebene Ableitung eines Meetings - nie die Rohaufnahme.",
    lang: "Das Meeting-Cockpit zeigt genau diese freigegebenen, indexierten Protokolle. Personenbezogene Passagen werden vor der Freigabe entfernt.",
  },
  dropzone: {
    titel: "Dropzone (Ablage-Eingang)",
    kurz: "Ein überwachter Eingangsordner: Dort abgelegte Dateien werden alle 10 Minuten gelesen, in Text umgewandelt und geprüft in die Wissensbasis übernommen.",
    lang: "Unlesbare oder unzulässige Dateien landen in Quarantäne statt im Index. Jede Übernahme erhält einen Herkunftsnachweis.",
  },
  kanban_freigabe: {
    titel: "Kanban-Freigabe (OpenProject-Karten)",
    kurz: "Ein Statuswechsel an einer OpenProject-Karte erzeugt einen Freigabe-Eintrag (Arbeitspaket, alt -> neu, Begründung). Die App schreibt dabei NICHTS nach OpenProject.",
    lang: "Auch nach erteilter Freigabe bleibt der Eintrag im Zustand 'freigegeben - wartet auf OpenProject-Übernahme', bis das echte Schreiben ausdrücklich freigeschaltet wird.",
  },
  formular_zuweisung: {
    titel: "Formular-Zuweisung",
    kurz: "Ein Formular wird einer Rolle zugewiesen (z. B. 'Kunde füllt aus'). Die Empfänger sehen dazu einen proaktiven Hinweis in ihren 'Nächsten Schritten'.",
  },
  reminder_scan: {
    titel: "Erinnerungs-Prüfung (15 Minuten)",
    kurz: "Ein Hintergrund-Lauf prüft alle 15 Minuten die eingestellten Auslöser (KPI-Schwellen, Verzug, Fälligkeiten) und erzeugt Hinweise in der Glocke.",
    lang: "Gegen Doppelmeldungen geschützt: Dieselbe Sache wird nicht mehrfach gemeldet; offene Punkte kommen zur Wiedervorlage.",
  },
  naechste_schritte: {
    titel: "Nächste Schritte (proaktive KI)",
    kurz: "Priorisierte Handlungsvorschläge auf der Startseite - berechnet aus Kennzahlen, Verzug, Arbeitspaketen und Prozessständen, jeweils mit Begründung und Direktlink.",
  },
  kundensicht: {
    titel: "Kundensicht / Beratersicht",
    kurz: "Zwei Darstellungen derselben Anwendung: Die Kundensicht verdichtet auf die freigegebenen Module; die Beratersicht zeigt zusätzlich die internen Steuerungswerkzeuge.",
    lang: "Berater:innen können die Kundensicht als Vorschau einschalten. Umgekehrt geht es nicht - die Sicht ist eine Verdichtung, kein Rechte-Upgrade.",
  },
  hilfe_center: {
    titel: "Hilfe-Center",
    kurz: "Diese Seite: Einstieg nach Rolle, alle Module erklärt, Zusammenwirken der Systeme, Agenten-Landkarte, Rollen-Matrix, FAQ und Glossar. Das ?-Symbol oben auf jeder Seite springt zum passenden Modul-Eintrag.",
  },
};

export function glossar(term: string): GlossarEintrag | undefined {
  return GLOSSAR[term];
}
